import React, { useEffect, useMemo } from "react";
import { Button, Col, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import StepsInfo from "../StepsInfo";
import Logo from "./img/domain.webp";
import Subdomain from "./subdomain";
import UserDomainView from "./userDomainView";
import { useDomains } from "../../../hooks/useDomains";
import { DomainOptionType } from "../../../constants";

export default function DomainSetup() {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const {
    selectedOption,
    setSelectedOption,
    isNextStepDisabled,
    setIsNextStepDisabled,
    isCustomStep,
    setIsCustomStep,
    stepView,
    setStepView,
    urlType,
    domainStatusData,
    domainStatusDataLoading,
    refetchDomainStatusData,
  } = useDomains({ id: projectId });

  const refetchStatusData = async () => {
    try {
      await refetchDomainStatusData();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    document.title = "New Project";
  }, []);

  const handleNextStepDisabled = (value: boolean) => {
    setIsNextStepDisabled(value);
  };

  const showCustomSecondStep = (value: boolean) => {
    setIsCustomStep(value);
  };

  const options = useMemo(
    () => [
      {
        value: DomainOptionType.Domain,
        label: (
          <>
            {t("projects.Use")} <span className="fw-bold">me-page.com</span>{" "}
            {t("projects.domain")}
          </>
        ),
      },
      // TODO: Hidden for all users for now, will be opened later.
      // {
      //   value: DomainOptionType.Subdomain,
      //   label: (
      //     <>
      //       {t("projects.Use subdomain")}{" "}
      //       <span className="fw-bold">me-page.com</span>
      //     </>
      //   ),
      // },
      {
        value: DomainOptionType.UserDomain,
        label: t("projects.Use my domain"),
      },
      {
        value: DomainOptionType.UserSubdomain,
        label: t("projects.Use my subdomain"),
      },
    ],
    [t]
  );

  const commonUserDomainViewProps = {
    domainId: +projectId,
    domainStatusData,
    refetchStatusData,
    showCustomSecondStep,
    stepView,
    setStepView,
    urlType,
  };

  if (domainStatusDataLoading)
    return <Spinner animation="border" role="status" />;

  return (
    <>
      <div className="px-lg-3">
        <h1 className="fs-1 fw-bold">{t("projects.Add New Project")}</h1>
        <div className="text-gray mb-4 mb-md-5">
          <Link to={`${lng}/projects`}>{t("payment.Projects")}</Link>
          <span className="px-2">/</span>
          {t("projects.Add New Project")}
        </div>
        <div className="text-dark bg-white p-4 p-md-5 rounded">
          <StepsInfo
            step={2}
            stepNames={[
              t("projects.Project Name"),
              t("projects.Setup Domain"),
              t("projects.Complite Setup"),
            ]}
            className="mb-md-5 mx-auto col-lg-10 col-xl-8 col-xxl-7"
          />
          <Col
            md={8}
            lg={isCustomStep ? 11 : 5}
            className={`secondStep mx-auto text-center mt-md-5${isCustomStep ? " px-lg-4" : ""}`}
          >
            {!isCustomStep && (
              <>
                <Col xs={7} md={12} className="mx-auto">
                  <img
                    src={Logo}
                    loading="lazy"
                    alt="New Domain"
                    className="img-fluid my-4 my-md-5 pb-md-2"
                  />
                </Col>
                <h3 className="mb-1 mb-md-3 text-capitalize">
                  {t("projects.Project Domain")}
                </h3>
                <p className="text-gray mx-auto mb-4 fs-7 pb-md-2">
                  {t("projects.Add domain for your project")}
                </p>
                <div className="d-flex flex-column align-items-start mb-2 fs-7">
                  {options.map(({ value, label }) => (
                    <Form.Label key={value}>
                      <Form.Check
                        type="radio"
                        label={label}
                        id={`domain-radio-${value}`}
                        checked={selectedOption === value}
                        onChange={() => setSelectedOption(value)}
                        disabled={!!urlType}
                      />
                    </Form.Label>
                  ))}
                </div>
              </>
            )}

            {selectedOption === DomainOptionType.Domain && (
              <div className="mb-4 text-start fs-7 fs-md-6">
                <span className="fw-bold">{t("projects.Your URL")}:</span>{" "}
                {window.location.origin}
                {lng}/projects/{projectId}
              </div>
            )}
            {selectedOption === DomainOptionType.Subdomain && (
              <Subdomain
                handleNextStepDisabled={handleNextStepDisabled}
                domainId={+projectId}
                domainStatusData={domainStatusData}
                refetchStatusData={refetchStatusData}
              />
            )}
            {selectedOption === DomainOptionType.UserDomain && (
              <UserDomainView
                {...commonUserDomainViewProps}
                urlType={urlType}
              />
            )}
            {selectedOption === DomainOptionType.UserSubdomain && (
              <UserDomainView {...commonUserDomainViewProps} isSubdomainMode />
            )}

            {!isCustomStep && (
              <div className="pt-md-2 gy-3 d-flex justify-content-center">
                <Col className="me-3 me-md-4">
                  <Link to={`${lng}/projects`}>
                    <Button
                      variant="outline-dark"
                      className="w-100 fw-normal dark-hover"
                    >
                      {t("projects.Skip")}
                    </Button>
                  </Link>
                </Col>
                <Col>
                  <Button
                    variant="primary"
                    className="w-100 text-white fw-bold order-0 order-lg-1"
                    onClick={() =>
                      navigate(`${lng}/projects/new/${projectId}/complete`)
                    }
                    disabled={isNextStepDisabled}
                  >
                    {t("projects.Next")}
                  </Button>
                </Col>
              </div>
            )}
          </Col>
        </div>
      </div>
    </>
  );
}
